import { supabase } from '../lib/supabase';

// API 기본 URL 정의
export const API_URL = process.env.REACT_APP_API_URL || 'https://bll-pro-git-update-version-khanyongs-projects.vercel.app';

// 디버깅을 위한 로그 추가
console.log('API_URL 설정됨:', API_URL);

// 세션 유효성 검사 및 자동 갱신
export const ensureValidSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('세션 확인 오류:', error);
      return null;
    }

    if (!session) {
      console.log('세션이 없습니다.');
      return null;
    }

    // 토큰 만료 시간 확인 (만료 5분 전에 갱신)
    const expiresAt = session.expires_at * 1000; // 초를 밀리초로 변환
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    if (expiresAt - now < fiveMinutes) {
      console.log('토큰이 곧 만료됩니다. 갱신을 시도합니다.');
      const { data: refreshedSession, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error('토큰 갱신 실패:', refreshError);
        return null;
      }
      
      console.log('토큰이 성공적으로 갱신되었습니다.');
      return refreshedSession.session;
    }

    return session;
  } catch (error) {
    console.error('세션 확인 중 예외 발생:', error);
    return null;
  }
};

// 세션 변경 감지 설정
supabase.auth.onAuthStateChange((event, session) => {
  console.log('Auth state changed:', event);
  
  if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
    // 로그아웃 시 처리
    console.log('사용자가 로그아웃되었습니다.');
  } else if (event === 'TOKEN_REFRESHED') {
    console.log('토큰이 자동으로 갱신되었습니다.');
  } else if (event === 'SIGNED_IN') {
    console.log('사용자가 로그인했습니다.');
  }
});

// API 요청 함수 (세션 검증 포함)
export const fetchWithToken = async (url, options = {}) => {
  console.log('API 요청 시작:', url);
  
  try {
    // 유효한 세션 확보
    const session = await ensureValidSession();
    
    if (!session) {
      throw new Error('유효한 인증 세션이 없습니다.');
    }

    // API 요청 실행
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API 요청 실패: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error('API 요청 오류:', error);
    throw error;
  }
};

// Supabase 쿼리 실행 전 세션 검증
export const executeWithValidSession = async (queryFunction) => {
  try {
    const session = await ensureValidSession();
    
    if (!session) {
      throw new Error('유효한 인증 세션이 없습니다.');
    }

    return await queryFunction();
  } catch (error) {
    console.error('세션 검증된 쿼리 실행 오류:', error);
    throw error;
  }
};

// 로그인 상태 확인 (사용자가 승인되었는지도 확인)
export const checkAuthStatus = async () => {
  try {
    const session = await ensureValidSession();
    
    if (!session?.user) {
      return { isAuthenticated: false, isApproved: false, user: null };
    }

    // 사용자 프로필에서 승인 상태 확인
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('is_approved, email_verified, role')
      .eq('id', session.user.id)
      .single();

    if (error) {
      console.error('프로필 확인 오류:', error);
      return { isAuthenticated: true, isApproved: false, user: session.user };
    }

    return {
      isAuthenticated: true,
      isApproved: profile?.is_approved || false,
      isEmailVerified: profile?.email_verified || false,
      user: session.user,
      profile: profile
    };
  } catch (error) {
    console.error('인증 상태 확인 오류:', error);
    return { isAuthenticated: false, isApproved: false, user: null };
  }
};

// 인증 관련 함수들
export const auth = {
  // 로그인
  signIn: async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      console.error('로그인 오류:', error);
      throw error;
    }

    return {
      user: data.user,
      session: data.session
    };
  },

  // 로그아웃
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('로그아웃 오류:', error);
      throw error;
    }
    
    // 로그아웃 후 홈페이지로 이동
    window.location.href = '/';
  },

  // 현재 사용자 조회
  getCurrentUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('사용자 조회 오류:', error);
      throw error;
    }
    return user;
  },

  // 세션 확인
  isAuthenticated: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return !!session;
  }
};

export const handlePasswordReset = async (email) => {
  // ... (이하 동일)
};

// 토큰 자동 갱신과 함께 안전한 API 호출을 위한 유틸리티 함수
export const getAuthenticatedUser = async () => {
  try {
    // 1. 현재 세션 확인
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('세션 확인 오류:', sessionError);
      return null;
    }

    // 2. 세션이 없으면 null 반환
    if (!session?.user) {
      return null;
    }

    // 3. 토큰 만료 체크 (만료 5분 전에 갱신)
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at;
    const refreshThreshold = 5 * 60; // 5분

    if (expiresAt && (expiresAt - now) < refreshThreshold) {
      console.log('토큰 만료 임박, 자동 갱신 시도...');
      
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error('토큰 갱신 실패:', refreshError);
        return null;
      }
      
      if (refreshData?.session?.user) {
        console.log('토큰 갱신 성공');
        return refreshData.session.user;
      }
    }

    return session.user;
  } catch (error) {
    console.error('인증 사용자 확인 중 오류:', error);
    return null;
  }
};

// API 호출 전 자동 인증 체크 및 갱신
export const withAuthRefresh = async (apiCall) => {
  try {
    const user = await getAuthenticatedUser();
    
    if (!user) {
      throw new Error('인증이 필요합니다. 다시 로그인해주세요.');
    }

    // API 호출 실행
    return await apiCall(user);
  } catch (error) {
    // 인증 관련 오류인 경우 토큰 갱신 재시도
    if (error.message?.includes('JWT') || error.message?.includes('token') || error.code === 'PGRST301') {
      console.log('인증 오류 감지, 토큰 갱신 후 재시도...');
      
      const { data: refreshData } = await supabase.auth.refreshSession();
      
      if (refreshData?.session?.user) {
        return await apiCall(refreshData.session.user);
      }
    }
    
    throw error;
  }
};

// 장시간 세션용 자동 저장 유틸리티
export const createAutoSaveSession = (saveFunction, intervalMinutes = 5) => {
  let autoSaveInterval = null;
  
  const startAutoSave = () => {
    if (autoSaveInterval) {
      clearInterval(autoSaveInterval);
    }
    
    autoSaveInterval = setInterval(async () => {
      try {
        console.log('자동 저장 시도...');
        const user = await getAuthenticatedUser();
        
        if (!user) {
          console.log('자동 저장 실패: 사용자 인증 필요');
          return;
        }
        
        await saveFunction(user);
        console.log('자동 저장 완료');
      } catch (error) {
        console.error('자동 저장 중 오류:', error);
      }
    }, intervalMinutes * 60 * 1000); // 분을 밀리초로 변환
  };
  
  const stopAutoSave = () => {
    if (autoSaveInterval) {
      clearInterval(autoSaveInterval);
      autoSaveInterval = null;
    }
  };
  
  return {
    startAutoSave,
    stopAutoSave
  };
};

// 세션 연장 알림 함수
export const showSessionExtensionNotice = (onExtend) => {
  const shouldShowNotice = localStorage.getItem('sessionExtensionShown') !== 'true';
  
  if (shouldShowNotice) {
    const userConfirmed = window.confirm(
      '장시간 작업 중입니다. 세션 유지를 위해 토큰이 자동으로 갱신됩니다.\n\n' +
      '계속 작업하시겠습니까?\n\n' +
      '(이 메시지는 세션당 한 번만 표시됩니다)'
    );
    
    if (userConfirmed) {
      localStorage.setItem('sessionExtensionShown', 'true');
      onExtend?.();
    }
    
    return userConfirmed;
  }
  
  return true;
};

// 세션 종료 시 정리 함수
export const cleanupSession = () => {
  localStorage.removeItem('sessionExtensionShown');
};